import React from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Check, CreditCard, Sparkles } from 'lucide-react';
import { cards } from '../../data/mock';

const cardColors = {
  silver: 'from-gray-400 to-gray-500',
  slate: 'from-slate-600 to-slate-700',
  amber: 'from-amber-400 via-yellow-400 to-amber-500'
};

const Cards = () => {
  return (
    <section id="cards" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#6B2C91] font-semibold text-sm uppercase tracking-wider">
            Nos Cartes
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-gray-900">
            Votre Carte KIPAY : Bien Plus Qu'une Carte Bancaire
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            La Carte KIPAY est une carte bancaire internationale co-brandée avec votre banque, 
            qui vous donne accès à tous les avantages de l'écosystème EKIP.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {cards.map((card) => (
            <Card
              key={card.id}
              className={`relative overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all duration-500 ${
                card.popular ? 'ring-2 ring-[#6B2C91] scale-105' : ''
              }`}
            >
              {card.popular && (
                <div className="absolute top-4 right-4 z-10">
                  <Badge className="bg-[#6B2C91] text-white">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Populaire
                  </Badge>
                </div>
              )}

              {/* Card Visual */}
              <div className={`h-48 bg-gradient-to-br ${cardColors[card.color]} relative overflow-hidden`}>
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent" />
                <div className="absolute inset-0 p-6 flex flex-col justify-between">
                  <div className="flex justify-between items-start">
                    <CreditCard className="w-10 h-10 text-white/80" />
                    <span className="text-white text-sm font-bold bg-white/20 px-3 py-1 rounded-full">
                      {card.tier.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="text-white/70 text-xs mb-1">CARTE KIPAY</div>
                    <div className="text-white font-mono text-sm tracking-wider">•••• •••• •••• ••••</div>
                  </div>
                </div>
              </div>

              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">KIPAY {card.tier}</h3>
                    <p className="text-sm text-gray-500">{card.income}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-[#6B2C91]">{card.discount}</div>
                    <div className="text-xs text-gray-500">de réduction</div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Price */}
                <div className="flex items-baseline">
                  <span className="text-2xl font-bold text-gray-900">{card.price.split('/')[0]}</span>
                  <span className="text-gray-500 ml-1">/an</span>
                </div>

                {/* Points rate */}
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-8 h-8 bg-[#FFD700]/20 rounded-full flex items-center justify-center">
                    <span className="text-[#FFD700] font-bold text-xs">PT</span>
                  </div>
                  <span className="text-gray-700">{card.points}</span>
                </div>

                {/* Benefits */}
                <ul className="space-y-3">
                  {card.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start text-sm text-gray-700">
                      <Check className="w-5 h-5 text-[#00C853] mr-2 flex-shrink-0 mt-0.5" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>

                {/* Limits */}
                <div className="pt-4 border-t border-gray-100">
                  <div className="grid grid-cols-2 gap-4 text-xs">
                    <div>
                      <span className="text-gray-500">Retrait</span>
                      <div className="font-semibold text-gray-900">{card.limits.withdrawal}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Paiement</span>
                      <div className="font-semibold text-gray-900">{card.limits.payment}</div>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <Button
                  className={`w-full ${
                    card.popular
                      ? 'bg-[#6B2C91] hover:bg-[#5a2579] text-white'
                      : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                >
                  {card.tier === 'Gold' ? 'Demander Ma Carte Gold' : `Commander Ma Carte ${card.tier}`}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Cards;
